import type { InPersonEvents } from 'types/in-person-events';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import {
  Button,
  TextFieldControlled as Input,
  Form,
} from '@leafwell/components';

import { useWordPressContext } from 'contexts/wordpress-provider';
import { useBrazeContext } from 'contexts/braze-context';
import { useEventsTracker } from 'contexts/eventsTracker';

import SignupSingleButton from './signup-single-button';
import WysiwygContent from './wysiwyg-content';

type InPersonEventModalProps = {
  inPersonEvents: InPersonEvents;
  currentState: string;
  formSubmitText?: string;
  className?: string;
};

function FormSubmitMessage() {
  return (
    <div className="text-center mt-2 mb-6 px-6">
      <p>Thank you for choosing Leafwell.</p>
      <p>Someone on our team will be in touch with you soon.</p>
    </div>
  );
}

const UpcomingEventItem: React.FC<{
  event: NonNullable<InPersonEvents['upcomingEvents']>[number];
  currentState?: string;
}> = ({ event, currentState }) => {
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [isClamped, setIsClamped] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (descriptionRef.current) {
      setIsClamped(
        descriptionRef.current.scrollHeight >
          descriptionRef.current.clientHeight,
      );
    }
  }, []);

  return (
    <li className="border border-gray-200 rounded-xl overflow-hidden flex items-stretch">
      {/* Date badge */}
      <div className="flex flex-col items-center justify-center bg-lilac-100 px-4 py-3 text-center w-[150px]">
        <p className="text-xs font-semibold uppercase tracking-wide text-purple-600">
          {event.month}
        </p>
        <p className="text-2xl font-bold leading-tight text-purple-600">
          {event.days}
        </p>
        <p className="text-xs font-semibold uppercase tracking-wide text-purple-600">
          {event.daysOfTheWeek}
        </p>
        <SignupSingleButton
          stateCode={currentState}
          formSubmitText="Book"
          className="!min-h-0 h-fit w-fit !p-1 !px-4 text-xs lg:text-sm mt-2"
          city={event.eventName}
          ctaLocation="in-person-event-modal"
        />
      </div>

      {/* Event info */}
      <div className="flex-1 min-w-0 p-4">
        <p className="font-semibold text-base">{event.eventName}</p>
        {event.eventSchedule && (
          <div className="flex items-center gap-x-1">
            <Image src="/clock.svg" alt="Clock" width={16} height={16} />
            <p className="text-xs mt-0.5">{event.eventSchedule}</p>
          </div>
        )}
        {event.eventDescription && (
          <div
            className={isClamped || isExpanded ? 'cursor-pointer' : ''}
            onClick={() => isClamped && setIsExpanded(!isExpanded)}
          >
            <p
              ref={descriptionRef}
              className={`text-xs lg:text-base font-medium text-purple-500 mt-1 ${
                isExpanded ? '' : 'line-clamp-1'
              }`}
              dangerouslySetInnerHTML={{ __html: event.eventDescription }}
            />
            {isClamped && !isExpanded && (
              <span className="text-xs font-medium text-gray-400">
                Read more
              </span>
            )}
          </div>
        )}
      </div>
    </li>
  );
};

const EventItem: React.FC<{
  event: InPersonEvents['locations'][number];
  currentState?: string;
}> = ({ event, currentState }) => {
  return (
    <li className="border border-gray-300 rounded-xl px-4 py-2 flex items-center justify-between gap-x-4">
      <div className="min-w-0 flex flex-col lg:flex-row lg:items-center lg:gap-x-6">
        <p className="font-semibold text-base w-[150px]">{event.location}</p>
        <WysiwygContent
          className="not-prose text-xs !text-purple-500"
          content={event.eventInformation}
        />
      </div>
      <SignupSingleButton
        stateCode={currentState}
        formSubmitText="Book"
        className="!min-h-0 h-fit w-fit !p-2 !px-4 text-xs lg:text-base"
        city={event.location}
        ctaLocation="in-person-event-modal"
      />
    </li>
  );
};

const InPersonEventModal: React.FC<InPersonEventModalProps> = ({
  inPersonEvents,
  currentState,
  formSubmitText,
  className,
}) => {
  const { translations } = useWordPressContext();
  const [isOpen, setIsOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const { trackEvent } = useEventsTracker();
  const { saveUser: saveBrazeUser, requestImmediateDataFlush } =
    useBrazeContext();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  useEffect(() => {
    const header = document.querySelector('#header-container');

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      header?.classList.add('!static');
      header?.classList.add('!hidden');
    } else {
      document.body.style.overflow = '';
      header?.classList.remove('!static');
      header?.classList.remove('!hidden');
    }

    return () => {
      document.body.style.overflow = '';
      header?.classList.remove('!static');
      header?.classList.remove('!hidden');
    };
  }, [isOpen]);

  const checkOverflow = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const hasOverflow = el.scrollHeight > el.clientHeight + 1;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 16;
    setShowScrollHint(hasOverflow && !atBottom);
  };

  useEffect(() => {
    const el = scrollContainerRef.current;

    if (!isOpen || !el) return;

    checkOverflow();

    const resizeObserver = new ResizeObserver(() => checkOverflow());
    resizeObserver.observe(el);

    let scrollTimeout: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(checkOverflow, 50);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      resizeObserver.disconnect();
      el.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [isOpen, showForm, inPersonEvents]);

  const handleWaitlist = ({ zipCode, wlEmail }) => {
    setIsLoading(true);
    trackEvent({
      event: 'form_submit',
      form_name: `formType_${window?.location?.pathname}`,
      form_destination: window?.location?.pathname,
      form_submit_text:
        formSubmitText && formSubmitText !== ''
          ? formSubmitText
          : translations['Submit'],
      formType: 'waiting_list_mc',
    });

    saveBrazeUser({
      email: wlEmail,
      customAttributes: {
        state: translations[currentState.toUpperCase()],
        zip_code: zipCode,
      },
    });

    requestImmediateDataFlush();
    setIsLoading(false);
    setFormSubmitted(true);
  };

  const buttonTitle = 'See available locations and dates';

  const hasUpcomingEvents =
    inPersonEvents?.upcomingEvents &&
    inPersonEvents?.upcomingEvents?.length > 0;

  const hasLocations =
    inPersonEvents?.locations && inPersonEvents?.locations?.length > 0;

  return (
    <>
      <button
        onClick={() => {
          trackEvent({
            event: 'select_content',
            content_type: buttonTitle,
            item_id: 'in_person_events_button',
            action: 'open_in_person_events_modal',
            origin: window?.location?.pathname,
          });

          setIsOpen(true);
        }}
        className={`flex items-center gap-x-2 px-4 py-3 bg-white border border-white rounded-md text-sm font-medium hover:bg-lilac-300 hover:border-lilac-300 transition-colors w-full ${className}`}
      >
        <Image
          src="/calendar-locations-icon.svg"
          alt=""
          width={20}
          height={20}
        />
        {buttonTitle}
      </button>
      {isOpen && (
        <div className="fixed left-0 right-0 bottom-0 top-0 z-50 flex items-start lg:items-center justify-center">
          {/* Backdrop */}
          <div className="absolute left-0 right-0 bottom-0 -top-32 bg-black/40" />

          {/* Modal */}
          <div className="relative z-10 bg-white rounded-none lg:rounded-2xl shadow-xl w-full h-full lg:h-auto max-h-full lg:max-h-[85vh] max-w-md lg:max-w-xl mx-4 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between py-4 px-6 bg-lilac-50 rounded-t-none lg:rounded-t-2xl flex-shrink-0">
              <p className="text-base font-semibold">
                {showForm
                  ? 'Join the waitlist'
                  : 'Available locations and dates'}
              </p>
              <button
                onClick={() => {
                  trackEvent({
                    event: 'select_content',
                    content_type: buttonTitle,
                    item_id: 'in_person_events_button',
                    action: 'close_in_person_events_modal',
                    origin: window?.location?.pathname,
                  });
                  setIsOpen(false);
                  setShowForm(false);
                }}
                className="flex items-center gap-x-2 text-sm font-medium"
              >
                CLOSE{' '}
                <Image src="/close.svg" alt="Close" width={16} height={16} />
              </button>
            </div>
            {!showForm ? (
              <>
                {/* Events list */}
                <div
                  ref={scrollContainerRef}
                  className="overflow-y-auto flex-1 min-h-0 px-6 flex flex-col gap-y-4 py-4"
                >
                  {hasUpcomingEvents && (
                    <div className="flex flex-col gap-y-2">
                      <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                        Upcoming Events
                      </p>
                      <ul className="flex flex-col gap-y-1 lg:gap-y-2">
                        {inPersonEvents.upcomingEvents.map((event, index) => (
                          <UpcomingEventItem
                            key={index}
                            event={event}
                            currentState={currentState}
                          />
                        ))}
                      </ul>
                    </div>
                  )}
                  {hasLocations && (
                    <div className="flex flex-col gap-y-2">
                      <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                        Regular Clinics
                      </p>
                      <ul className="flex flex-col gap-y-1 lg:gap-y-2">
                        {inPersonEvents.locations.map((event, index) => (
                          <EventItem
                            key={index}
                            event={event}
                            currentState={currentState}
                          />
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center gap-x-1">
                    <Image
                      src="/location.svg"
                      alt="Notification"
                      width={16}
                      height={16}
                    />
                    <p className="text-xs">
                      Don't see your city?{' '}
                      <button
                        className="text-sm font-medium underline text-purple-600"
                        onClick={() => setShowForm(true)}
                      >
                        Get notified
                      </button>
                    </p>
                  </div>
                </div>
                {showScrollHint && (
                  <div className="relative flex justify-center pointer-events-none">
                    <button
                      type="button"
                      onClick={() => {
                        scrollContainerRef.current?.scrollBy({
                          top: 200,
                          behavior: 'smooth',
                        });
                      }}
                      className="absolute -top-6 z-30 pointer-events-auto flex items-center gap-x-1.5 bg-white border border-gray-300 shadow-md rounded-full px-3 py-1.5 text-base font-medium text-gray-700"
                    >
                      More clinics
                      <span className="flex items-center justify-center w-6 h-6 rounded-full py-0.5 bg-lilac-100">
                        <Image
                          src="/arrow-vertical.svg"
                          alt=""
                          width={10}
                          height={10}
                        />
                      </span>
                    </button>
                  </div>
                )}
                {/* Footer */}
                <div className="flex items-center justify-center gap-y-3 pt-2 pb-6 px-6 flex-shrink-0">
                  <SignupSingleButton
                    stateCode={currentState}
                    formSubmitText="Book your spot"
                    className="w-full lg:w-64"
                    ctaLocation="in-person-event-modal"
                  />
                </div>
              </>
            ) : formSubmitted ? (
              <FormSubmitMessage />
            ) : (
              /* Waitlist form */
              <>
                <p className="text-center my-2 px-6">
                  Sign up today and we'll email you as soon as we have a free
                  medical card event in your city
                </p>
                <Form
                  className="mb-6 mt-4 px-6"
                  mode="onSubmit"
                  defaultValues={{ zipCode: '', wlEmail: '' }}
                  onSubmit={handleWaitlist}
                >
                  <div className="grid gap-y-2 my-4">
                    <p className="font-medium">
                      {translations['Zip Code']} <sup>*</sup>
                    </p>
                    <Input
                      className="rounded-lg"
                      name="zipCode"
                      placeholder={`${translations['Zip Code']} *`}
                      type="number"
                      validations={['required', 'zipcode']}
                    />
                  </div>
                  <div className="grid gap-y-2 my-4">
                    <p className="font-medium">
                      {translations['Email']} <sup>*</sup>
                    </p>
                    <Input
                      className="rounded-lg"
                      name="wlEmail"
                      placeholder="Email *"
                      type="email"
                      validations={['required', 'email']}
                    />
                  </div>
                  <Button
                    type="submit"
                    label="Keep me updated"
                    variant="secondary"
                    name="commit_2"
                    id="submit_waiting_list"
                    className="mt-2 w-full rounded-lg"
                    loading={isLoading}
                    data-disable-with="Keep me updated"
                  />
                </Form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default InPersonEventModal;
